import React from 'react';
import { useCountsData } from './hooks/useCountsData';
import CountsDisplay from './components/CountsDisplay';
import StatusBadge from './components/StatusBadge';
import ActionCellHeader from './components/ActionCellHeader';
import { UI_CONSTANTS } from './constants';
import { DateRange } from './types';

interface CountsRowProps {
  dateRange: DateRange;
}

const CountsRow: React.FC<CountsRowProps> = ({ dateRange }) => {
  const { loading, qboTotals, eservTotals, diffs, handleGetCounts } = useCountsData(dateRange);
  
  // Debug logging
  console.log('🔍 CountsRow - EServ totals:', eservTotals);
  console.log('🔍 CountsRow - QBO totals:', qboTotals);
  console.log('🔍 CountsRow - Diffs:', diffs);

  const getStatus = () => {
    if (loading) return 'loading';
    if (qboTotals && eservTotals) return 'success';
    return 'ready';
  };
  
  const getStatusText = () => {
    if (loading) return 'Loading';
    if (qboTotals && eservTotals) return 'Success';
    return 'Ready';
  };

  return (
    <tr>
      <td className="border border-gray-300 px-3 py-2 font-medium">2. Get QBO Counts</td>
      <td className="border border-gray-300 px-3 py-2 relative">
        <ActionCellHeader description="Fetches and compares counts between QuickBooks Online and EServ database for invoices, payments, and customers within the selected date range." />
        <div className="flex justify-end">
          <button
            onClick={handleGetCounts}
            className="px-2 py-0.5 border border-blue-600 text-blue-600 text-xs rounded hover:bg-blue-50 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Get Counts'}
          </button>
        </div>
      </td>
      <td className={`border border-gray-300 px-3 py-2 ${UI_CONSTANTS.MAX_DATA_WIDTH}`}>
        <CountsDisplay data={eservTotals} />
      </td>
      <td className={`border border-gray-300 px-3 py-2 ${UI_CONSTANTS.MAX_DATA_WIDTH}`}>
        <CountsDisplay data={qboTotals} />
      </td>
      <td className="border border-gray-300 px-3 py-2">
        <div className="font-mono text-xs">
          {loading ? (
            <div className="text-gray-500">Fetching...</div>
          ) : diffs ? (
            <div className="bg-gray-50 p-2 rounded border space-y-1">
              <div className="flex justify-between">
                <span>Invoices:</span>
                <span className={`font-semibold ${diffs.invoices.difference === 0 ? 'text-green-600' : 'text-orange-600'}`}>
                  {diffs.invoices.difference > 0 ? '+' : ''}{diffs.invoices.difference}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Payments:</span>
                <span className={`font-semibold ${diffs.payments.difference === 0 ? 'text-green-600' : 'text-orange-600'}`}>
                  {diffs.payments.difference > 0 ? '+' : ''}{diffs.payments.difference}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Customers:</span>
                <span className={`font-semibold ${diffs.customers.difference === 0 ? 'text-green-600' : 'text-orange-600'}`}>
                  {diffs.customers.difference > 0 ? '+' : ''}{diffs.customers.difference}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-gray-500">-</div>
          )}
        </div>
      </td>
      <td className="border border-gray-300 px-2 py-2 text-center">
        <StatusBadge status={getStatus()} text={getStatusText()} />
      </td>
      <td className="border border-gray-300 px-3 py-2">
        <div className="text-gray-500 text-xs">-</div>
      </td>
      <td className="border border-gray-300 px-3 py-2">
        <div className="text-gray-500 text-xs">-</div>
      </td>
    </tr>
  );
};

export default CountsRow;
